import React, { useState, useRef } from "react";
import ReactCrop, { Crop, PixelCrop } from "react-image-crop";
import { canvasPreview } from "./canvasPreview";
import { useTranslation } from "react-i18next";
import { ErrorMessage, Field, useFormikContext } from "formik";
import { CustomModal, TooltipCustom } from "@services/ui-components";

interface UploadImageProps {
    name: string;
    value?: string;
    additionalInfo?: string;
    title?: string;
}

const UploadImage: React.FC<UploadImageProps> = ({ name, title = "Clients photo", value, additionalInfo }) => {
    const { t } = useTranslation();
    const { setFieldValue, setFieldError } = useFormikContext();
    const [showModal, setShowModal] = useState(false);

    const [imgSrc, setImgSrc] = useState("");
    const previewCanvasRef = useRef<HTMLCanvasElement>(null);
    const imgRef = useRef<HTMLImageElement>(null);
    const [crop, setCrop] = useState<Crop>();
    const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
    const [errorSize, setErrorSize] = useState("");
    const [isSaved, setIsSaved] = useState(false);

    const handleModalClose = () => setShowModal(false);

    const onSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        setErrorSize("");
        const MAX_FILE_SIZE = 3 * 1024 * 1024;
        if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0];
            if (file.size > MAX_FILE_SIZE) {
                setFieldValue(name, null);
                setErrorSize(t("validation.the file is too large. Max size is 3 MB"));
            } else {
                setCrop(undefined); //Reset the previous crop
                const reader = new FileReader();

                reader.addEventListener("load", () => setImgSrc(reader.result?.toString() || ""));
                reader.readAsDataURL(e.target.files[0]);
                setShowModal(true);
                setFieldError(name, ""); // Reset error message
            }
        }
    };

    const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
        const { width, height } = e.currentTarget;
        // Calculate the size of the smallest side to create a square
        const size = Math.min(width, height) * 0.8;

        // Set the initial state of the crop with dimensions in pixels
        const initialCrop: Crop = {
            unit: "px", // use pixels instead of percentages
            width: size,
            height: size,
            x: (width - size) / 2, // Center the crop horizontally
            y: (height - size) / 2, // Center the crop vertically
        };

        setCrop(initialCrop); // Set this frame as the initial state
    };

    const handleChangeImage = () => {
        setErrorSize("");
        const fileInput = document.getElementById(name);
        fileInput?.click();
    };

    const handleDeleteImage = () => {
        setErrorSize("");
        setFieldValue(name, ""); // clear the form field
        setImgSrc(""); // remove the image from the state, which will remove it from the preview
        setCompletedCrop(null); // reset the cropping area
        setIsSaved(false); // to be able to upload a new image
        if (previewCanvasRef.current) {
            const ctx = previewCanvasRef.current.getContext("2d");
            if (ctx) {
                // Cleaning the canvas
                ctx.clearRect(0, 0, previewCanvasRef.current.width, previewCanvasRef.current.height);
            }
        }
    };

    const handleSaveImage = async () => {
        const image = imgRef.current;
        const previewCanvas = previewCanvasRef.current;
        if (!image || !previewCanvas || !completedCrop) {
            throw new Error("Crop canvas does not exist");
        }

        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;

        const offscreen = new OffscreenCanvas(completedCrop.width * scaleX, completedCrop.height * scaleY);
        const ctx = offscreen.getContext("2d");
        if (!ctx) {
            throw new Error("No 2d context");
        }

        ctx.drawImage(
            previewCanvas,
            0,
            0,
            previewCanvas.width,
            previewCanvas.height,
            0,
            0,
            offscreen.width,
            offscreen.height
        );

        const blob = await offscreen.convertToBlob({
            type: "image/png",
        });
        const file = new File([blob], "cropped-image.png", { type: "image/png" });
        setFieldValue(name, file);

        const objectUrl = URL.createObjectURL(blob);
        setImgSrc(objectUrl); // Update preview
        const reader = new FileReader();
        reader.onloadend = () => {
            const dataUrl = reader.result;
        };
        reader.readAsDataURL(blob);
        canvasPreview(image, previewCanvas, completedCrop, scaleX, scaleY);
        setIsSaved(true);
        setShowModal(false);
    };

    return (
        <div className="mb-3 col-12" style={{ maxWidth: "325px" }}>
            <label
                htmlFor={name}
                className="d-flex justify-content-center rounded-1 align-items-center border p-4 w-100"
                style={{
                    maxWidth: "325px",
                    height: "325px",
                    cursor: isSaved ? "" : "pointer",
                }}
            >
                {!completedCrop ? (
                    <div className="text-center">
                        {value ? (
                            <img src={`https://mepos-2.dev.me-team/${value}`} alt="Photo" />
                        ) : (
                            <div className="text-center">
                                {" "}
                                <i className="bi bi-plus-lg mb-2 d-block fs-7"></i>
                                <span>{t("Add")}</span>{" "}
                            </div>
                        )}
                    </div>
                ) : (
                    <canvas
                        ref={previewCanvasRef}
                        onClick={(e) => {
                            if (isSaved) {
                                e.preventDefault();
                            }
                        }}
                        style={{
                            display: completedCrop ? "block" : "none",
                            width: "100%",
                            height: "100%",
                            objectFit: "fill",
                        }}
                    />
                )}
                <Field
                    id={name}
                    name={name}
                    type="file"
                    value={undefined}
                    className="d-none form-control "
                    accept="image/png, image/jpeg, image/jpg"
                    onChange={onSelectFile}
                />
            </label>
            <div className="text-danger mt-3 fs-7">{t(errorSize)}</div>
            <ErrorMessage name="image">{(msg) => <div className="invalid-feedback d-block">{msg}</div>}</ErrorMessage>
            {additionalInfo && <div className="text-grey mt-3 fs-7">{additionalInfo}</div>}
            <div className={`text-grey ${!additionalInfo ? "mt-3" : ""} fs-7`}>{t("Maximum size: 3 MB")}</div>
            {isSaved && (
                <div className="gap-3 d-flex mt-4" style={{ height: "32px" }}>
                    <button type="button" className="btn  btn-outline-primary btn-sm px-3" onClick={handleChangeImage}>
                        {t("Change")}
                    </button>
                    <TooltipCustom text={t("Delete image")} placement="top">
                        <button className="btn  btn-outline-primary btn-sm px-2" onClick={handleDeleteImage}>
                            <i className="bi bi-trash3-fill"></i>
                        </button>
                    </TooltipCustom>
                </div>
            )}
            <CustomModal
                show={showModal}
                handleClose={handleModalClose}
                title={t(title)}
                variantTitle="primary"
                body={
                    <div style={{ width: "430px", margin: "0 auto" }}>
                        {!!imgSrc && (
                            <ReactCrop
                                crop={crop}
                                onChange={(_, percentCrop) => setCrop(percentCrop)}
                                onComplete={(c) => setCompletedCrop(c)}
                                className="w-100 h-100"
                                aspect={1}
                                maxWidth={300}
                                minWidth={200}
                            >
                                <img
                                    ref={imgRef}
                                    alt="Crop me"
                                    src={imgSrc}
                                    className="w-100 h-100 "
                                    onLoad={onImageLoad}
                                />
                            </ReactCrop>
                        )}
                    </div>
                }
                buttons={[
                    {
                        text: t("Save"),
                        onClick: handleSaveImage,
                        variant: "primary",
                        className: "w-100",
                    },
                ]}
            />
        </div>
    );
};

export default UploadImage;
